import { useState } from 'react';
import { useCustom, useCustomMutation } from '@refinedev/core';
import {
  Loader2,
  Plus,
  Award,
  History,
  Minus,
  ToggleLeft,
  ListPlus,
  ListMinus,
  Edit3,
  XCircle,
  Clock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { notify } from '@/lib/notify';
import { resolveApiUrl } from '@/providers/api-url';

const API_URL = resolveApiUrl();

// --- Types ---

type BonusType = 'NUMBER' | 'BOOLEAN' | 'ARRAY' | 'STRING';
type BonusStatus = 'ACTIVE' | 'EXPIRED' | 'REVOKED';
type BonusActionType =
  | 'CREATE'
  | 'INCREMENT'
  | 'DECREMENT'
  | 'SET'
  | 'TOGGLE'
  | 'PUSH'
  | 'REMOVE'
  | 'EXPIRE'
  | 'REVOKE';

interface UserBonus {
  id: number;
  userId: number;
  bonusTemplateId: number | null;
  name: string;
  type: BonusType;
  description: string | null;
  currentValue: number | boolean | string[] | string;
  initialValue: number | boolean | string[] | string;
  status: BonusStatus;
  expiresAt: string | null;
  createdAt: string;
}

interface BonusAction {
  id: number;
  userBonusId: number;
  action: BonusActionType;
  previousValue: number | boolean | string[] | string | null;
  newValue: number | boolean | string[] | string | null;
  reason: string | null;
  adminUserId: number | null;
  createdAt: string;
}

interface UserBonusesProps {
  userId: number;
}

type StatusFilter = 'ALL' | BonusStatus;

// --- Helpers ---

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const formatDateTime = (dateString: string) => {
  return new Date(dateString).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const renderBonusValue = (value: number | boolean | string[] | string, type: BonusType): string => {
  switch (type) {
    case 'NUMBER':
      return String(value);
    case 'BOOLEAN':
      return value ? 'Да' : 'Нет';
    case 'ARRAY':
      if (Array.isArray(value)) {
        return value.length > 0 ? value.join(', ') : '(пусто)';
      }
      return String(value);
    case 'STRING':
      return String(value) || '(пусто)';
  }
};

const translateBonusType = (type: BonusType): string => {
  const map: Record<BonusType, string> = {
    NUMBER: 'Число',
    BOOLEAN: 'Логический',
    ARRAY: 'Массив',
    STRING: 'Строка',
  };
  return map[type];
};

const translateActionType = (action: BonusActionType): string => {
  const map: Record<BonusActionType, string> = {
    CREATE: 'Создание',
    INCREMENT: 'Увеличение',
    DECREMENT: 'Уменьшение',
    SET: 'Установка',
    TOGGLE: 'Переключение',
    PUSH: 'Добавление',
    REMOVE: 'Удаление',
    EXPIRE: 'Истечение',
    REVOKE: 'Отзыв',
  };
  return map[action];
};

const getStatusBadge = (status: BonusStatus) => {
  switch (status) {
    case 'ACTIVE':
      return <Badge className="bg-green-500/10 text-green-500">Активен</Badge>;
    case 'EXPIRED':
      return <Badge variant="secondary">Истёк</Badge>;
    case 'REVOKED':
      return <Badge variant="destructive">Отозван</Badge>;
  }
};

// --- Component ---

export function UserBonuses({ userId }: UserBonusesProps) {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [setValueDialogOpen, setSetValueDialogOpen] = useState(false);
  const [pushDialogOpen, setPushDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedBonus, setSelectedBonus] = useState<UserBonus | null>(null);

  // Create form state
  const [createName, setCreateName] = useState('');
  const [createType, setCreateType] = useState<BonusType>('NUMBER');
  const [createInitialNumber, setCreateInitialNumber] = useState('0');
  const [createInitialBoolean, setCreateInitialBoolean] = useState(false);
  const [createInitialText, setCreateInitialText] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createDurationDays, setCreateDurationDays] = useState('');

  // Action dialog state
  const [setValueInput, setSetValueInput] = useState('');
  const [pushValueInput, setPushValueInput] = useState('');
  const [removeValueInput, setRemoveValueInput] = useState('');
  const [revokeReason, setRevokeReason] = useState('');

  // Fetch bonuses
  const bonusesResult = useCustom<UserBonus[]>({
    url: `${API_URL}/user-bonuses/admin/users/${userId}/bonuses`,
    method: 'get',
    config: {
      query: {
        ...(statusFilter !== 'ALL' ? { status: statusFilter } : {}),
        page: 1,
        limit: 100,
      },
    },
  });
  const isLoading = bonusesResult.query.isLoading;
  const refetch = bonusesResult.query.refetch;

  const bonuses: UserBonus[] = bonusesResult.query.data?.data ?? [];

  // Fetch history (lazy)
  const historyResult = useCustom<BonusAction[]>({
    url: `${API_URL}/user-bonuses/admin/${selectedBonus?.id ?? 0}/actions`,
    method: 'get',
    queryOptions: {
      enabled: historyDialogOpen && selectedBonus !== null,
    },
  });
  const historyLoading = historyResult.query.isLoading;
  const refetchHistory = historyResult.query.refetch;

  const historyActions: BonusAction[] = historyResult.query.data?.data ?? [];

  // Mutations
  const { mutate: createBonus, mutation: createMutation } = useCustomMutation();
  const { mutate: performAction, mutation: actionMutation } = useCustomMutation();
  const { mutate: revokeBonus, mutation: revokeMutation } = useCustomMutation();
  const createLoading = createMutation.isPending;
  const actionLoading = actionMutation.isPending;
  const revokeLoading = revokeMutation.isPending;

  // --- Handlers ---

  const handleCreateOpen = () => {
    setCreateName('');
    setCreateType('NUMBER');
    setCreateInitialNumber('0');
    setCreateInitialBoolean(false);
    setCreateInitialText('');
    setCreateDescription('');
    setCreateDurationDays('');
    setCreateDialogOpen(true);
  };

  const getCreateInitialValue = (): number | boolean | string[] | string => {
    switch (createType) {
      case 'NUMBER':
        return Number(createInitialNumber) || 0;
      case 'BOOLEAN':
        return createInitialBoolean;
      case 'ARRAY':
        return createInitialText
          ? createInitialText
              .split('\n')
              .map((s) => s.trim())
              .filter(Boolean)
          : [];
      case 'STRING':
        return createInitialText;
    }
  };

  const handleCreate = () => {
    if (!createName.trim()) {
      notify.error('Укажите название привилегии');
      return;
    }

    createBonus(
      {
        url: `${API_URL}/user-bonuses/admin`,
        method: 'post',
        values: {
          userId,
          name: createName.trim(),
          type: createType,
          initialValue: getCreateInitialValue(),
          ...(createDescription.trim() ? { description: createDescription.trim() } : {}),
          ...(createDurationDays ? { durationDays: Number(createDurationDays) } : {}),
        },
      },
      {
        onSuccess: () => {
          setCreateDialogOpen(false);
          refetch();
          notify.success('Привилегия создана');
        },
        onError: () => {
          notify.error('Ошибка при создании привилегии');
        },
      },
    );
  };

  const handleAction = (
    bonusId: number,
    action: string,
    value?: number | boolean | string | string[],
  ) => {
    performAction(
      {
        url: `${API_URL}/user-bonuses/admin/${bonusId}`,
        method: 'patch',
        values: {
          action,
          ...(value !== undefined ? { value } : {}),
          idempotencyKey: crypto.randomUUID(),
        },
      },
      {
        onSuccess: () => {
          refetch();
          notify.success('Действие выполнено');
        },
        onError: () => {
          notify.error('Ошибка при выполнении действия');
        },
      },
    );
  };

  const handleSetValue = () => {
    if (!selectedBonus) {
      return;
    }

    let parsedValue: number | string;
    if (selectedBonus.type === 'NUMBER') {
      parsedValue = Number(setValueInput);
      if (Number.isNaN(parsedValue)) {
        notify.error('Укажите корректное число');
        return;
      }
    } else {
      parsedValue = setValueInput;
    }

    handleAction(selectedBonus.id, 'SET', parsedValue);
    setSetValueDialogOpen(false);
  };

  const handlePush = () => {
    if (!selectedBonus || !pushValueInput.trim()) {
      return;
    }
    handleAction(selectedBonus.id, 'PUSH', pushValueInput.trim());
    setPushDialogOpen(false);
    setPushValueInput('');
  };

  const handleRemove = () => {
    if (!selectedBonus || !removeValueInput.trim()) {
      return;
    }
    handleAction(selectedBonus.id, 'REMOVE', removeValueInput.trim());
    setRemoveDialogOpen(false);
    setRemoveValueInput('');
  };

  const handleRevoke = () => {
    if (!selectedBonus) {
      return;
    }
    revokeBonus(
      {
        url: `${API_URL}/user-bonuses/admin/${selectedBonus.id}/revoke`,
        method: 'patch',
        values: (revokeReason.trim() ? { reason: revokeReason.trim() } : {}),
      },
      {
        onSuccess: () => {
          setRevokeDialogOpen(false);
          setRevokeReason('');
          refetch();
          notify.success('Привилегия отозвана');
        },
        onError: () => {
          notify.error('Ошибка при отзыве привилегии');
        },
      },
    );
  };

  const openSetValueDialog = (bonus: UserBonus) => {
    setSelectedBonus(bonus);
    setSetValueInput(String(bonus.currentValue));
    setSetValueDialogOpen(true);
  };

  const openPushDialog = (bonus: UserBonus) => {
    setSelectedBonus(bonus);
    setPushValueInput('');
    setPushDialogOpen(true);
  };

  const openRemoveDialog = (bonus: UserBonus) => {
    setSelectedBonus(bonus);
    setRemoveValueInput('');
    setRemoveDialogOpen(true);
  };

  const openRevokeDialog = (bonus: UserBonus) => {
    setSelectedBonus(bonus);
    setRevokeReason('');
    setRevokeDialogOpen(true);
  };

  const openHistoryDialog = (bonus: UserBonus) => {
    setSelectedBonus(bonus);
    setHistoryDialogOpen(true);
    refetchHistory();
  };

  // --- Render ---

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5" />
            Привилегии пользователя
          </CardTitle>
          <div className="flex items-center gap-2">
            <Select
              value={statusFilter}
              onValueChange={(value) => setStatusFilter(value as StatusFilter)}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Все статусы</SelectItem>
                <SelectItem value="ACTIVE">Активные</SelectItem>
                <SelectItem value="EXPIRED">Истёкшие</SelectItem>
                <SelectItem value="REVOKED">Отозванные</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleCreateOpen}>
              <Plus className="mr-2 h-4 w-4" />
              Добавить привилегию
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {bonuses.length === 0 ? (
            <div className="text-muted-foreground py-8 text-center">
              <Award className="mx-auto mb-2 h-12 w-12 opacity-50" />
              <p>Нет привилегий</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Название</TableHead>
                  <TableHead>Тип</TableHead>
                  <TableHead>Значение</TableHead>
                  <TableHead>Статус</TableHead>
                  <TableHead>Истекает</TableHead>
                  <TableHead className="text-right">Действия</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bonuses.map((bonus) => (
                  <TableRow key={bonus.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{bonus.name}</div>
                        {bonus.description && (
                          <div className="text-muted-foreground mt-0.5 text-xs">
                            {bonus.description}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{translateBonusType(bonus.type)}</Badge>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-sm">
                        {renderBonusValue(bonus.currentValue, bonus.type)}
                      </span>
                    </TableCell>
                    <TableCell>{getStatusBadge(bonus.status)}</TableCell>
                    <TableCell>
                      {bonus.expiresAt ? (
                        <div className="text-muted-foreground flex items-center gap-1 text-sm">
                          <Clock className="h-3 w-3" />
                          {formatDate(bonus.expiresAt)}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">Бессрочно</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {bonus.status === 'ACTIVE' && (
                          <>
                            {bonus.type === 'NUMBER' && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleAction(bonus.id, 'INCREMENT')}
                                  disabled={actionLoading}
                                  title="Увеличить на 1"
                                >
                                  <Plus className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleAction(bonus.id, 'DECREMENT')}
                                  disabled={actionLoading}
                                  title="Уменьшить на 1"
                                >
                                  <Minus className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openSetValueDialog(bonus)}
                                  disabled={actionLoading}
                                  title="Установить значение"
                                >
                                  <Edit3 className="h-3 w-3" />
                                </Button>
                              </>
                            )}
                            {bonus.type === 'BOOLEAN' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleAction(bonus.id, 'TOGGLE')}
                                disabled={actionLoading}
                                title="Переключить"
                              >
                                <ToggleLeft className="h-3 w-3" />
                              </Button>
                            )}
                            {bonus.type === 'ARRAY' && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openPushDialog(bonus)}
                                  disabled={actionLoading}
                                  title="Добавить элемент"
                                >
                                  <ListPlus className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openRemoveDialog(bonus)}
                                  disabled={actionLoading}
                                  title="Удалить элемент"
                                >
                                  <ListMinus className="h-3 w-3" />
                                </Button>
                              </>
                            )}
                            {bonus.type === 'STRING' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openSetValueDialog(bonus)}
                                disabled={actionLoading}
                                title="Установить значение"
                              >
                                <Edit3 className="h-3 w-3" />
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openRevokeDialog(bonus)}
                              className="text-destructive hover:text-destructive"
                              title="Отозвать"
                            >
                              <XCircle className="h-3 w-3" />
                            </Button>
                          </>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openHistoryDialog(bonus)}
                          title="История"
                        >
                          <History className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Bonus Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Добавить привилегию</DialogTitle>
            <DialogDescription>Создайте новую привилегию для пользователя.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Название *</Label>
              <Input
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="Например: Скидка на курс"
              />
            </div>
            <div className="space-y-2">
              <Label>Тип</Label>
              <Select
                value={createType}
                onValueChange={(value) => setCreateType(value as BonusType)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NUMBER">Число</SelectItem>
                  <SelectItem value="BOOLEAN">Логический</SelectItem>
                  <SelectItem value="ARRAY">Массив</SelectItem>
                  <SelectItem value="STRING">Строка</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Начальное значение</Label>
              {createType === 'NUMBER' && (
                <Input
                  type="number"
                  value={createInitialNumber}
                  onChange={(e) => setCreateInitialNumber(e.target.value)}
                />
              )}
              {createType === 'BOOLEAN' && (
                <div className="flex items-center gap-2">
                  <Switch
                    checked={createInitialBoolean}
                    onCheckedChange={setCreateInitialBoolean}
                  />
                  <span className="text-sm">{createInitialBoolean ? 'Да' : 'Нет'}</span>
                </div>
              )}
              {createType === 'ARRAY' && (
                <Textarea
                  value={createInitialText}
                  onChange={(e) => setCreateInitialText(e.target.value)}
                  placeholder="Каждый элемент на новой строке"
                  rows={3}
                />
              )}
              {createType === 'STRING' && (
                <Textarea
                  value={createInitialText}
                  onChange={(e) => setCreateInitialText(e.target.value)}
                  placeholder="Текстовое значение"
                  rows={2}
                />
              )}
            </div>
            <div className="space-y-2">
              <Label>Описание</Label>
              <Textarea
                value={createDescription}
                onChange={(e) => setCreateDescription(e.target.value)}
                placeholder="Необязательное описание"
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Срок действия (дней)</Label>
              <Input
                type="number"
                value={createDurationDays}
                onChange={(e) => setCreateDurationDays(e.target.value)}
                placeholder="Оставьте пустым для бессрочного"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Отмена
            </Button>
            <Button onClick={handleCreate} disabled={createLoading || !createName.trim()}>
              {createLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Создать
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Value Dialog */}
      <Dialog open={setValueDialogOpen} onOpenChange={setSetValueDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Установить значение</DialogTitle>
            <DialogDescription>Привилегия: {selectedBonus?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Новое значение</Label>
              {selectedBonus?.type === 'NUMBER' ? (
                <Input
                  type="number"
                  value={setValueInput}
                  onChange={(e) => setSetValueInput(e.target.value)}
                />
              ) : (
                <Textarea
                  value={setValueInput}
                  onChange={(e) => setSetValueInput(e.target.value)}
                  rows={3}
                />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSetValueDialogOpen(false)}>
              Отмена
            </Button>
            <Button onClick={handleSetValue} disabled={actionLoading}>
              {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Установить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Push Element Dialog */}
      <Dialog open={pushDialogOpen} onOpenChange={setPushDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Добавить элемент</DialogTitle>
            <DialogDescription>Привилегия: {selectedBonus?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Элемент</Label>
              <Input
                value={pushValueInput}
                onChange={(e) => setPushValueInput(e.target.value)}
                placeholder="Введите значение"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPushDialogOpen(false)}>
              Отмена
            </Button>
            <Button onClick={handlePush} disabled={actionLoading || !pushValueInput.trim()}>
              {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Добавить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Element Dialog */}
      <Dialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Удалить элемент</DialogTitle>
            <DialogDescription>Привилегия: {selectedBonus?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Элемент для удаления</Label>
              <Input
                value={removeValueInput}
                onChange={(e) => setRemoveValueInput(e.target.value)}
                placeholder="Введите значение для удаления"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveDialogOpen(false)}>
              Отмена
            </Button>
            <Button onClick={handleRemove} disabled={actionLoading || !removeValueInput.trim()}>
              {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Удалить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke Dialog */}
      <AlertDialog open={revokeDialogOpen} onOpenChange={setRevokeDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Отозвать привилегию?</AlertDialogTitle>
            <AlertDialogDescription>
              Вы уверены, что хотите отозвать привилегию &quot;{selectedBonus?.name}&quot;? Это действие
              нельзя отменить.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Label>Причина (необязательно)</Label>
            <Textarea
              value={revokeReason}
              onChange={(e) => setRevokeReason(e.target.value)}
              placeholder="Укажите причину отзыва"
              rows={2}
              className="mt-2"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRevoke}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {revokeLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Отозвать
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>История действий</DialogTitle>
            <DialogDescription>Привилегия: {selectedBonus?.name}</DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[400px]">
            {historyLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : (historyActions.length === 0 ? (
              <div className="text-muted-foreground py-8 text-center">
                <p>Нет записей</p>
              </div>
            ) : (
              <div className="space-y-3">
                {historyActions.map((action, index) => (
                  <div key={action.id}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {translateActionType(action.action)}
                          </Badge>
                          {action.reason && (
                            <span className="text-muted-foreground text-xs">{action.reason}</span>
                          )}
                        </div>
                        <div className="text-muted-foreground text-xs">
                          {action.previousValue !== null && action.previousValue !== undefined && (
                            <span>{String(action.previousValue)}</span>
                          )}
                          {action.previousValue !== null &&
                            action.previousValue !== undefined &&
                            action.newValue !== null &&
                            action.newValue !== undefined && <span> → </span>}
                          {action.newValue !== null && action.newValue !== undefined && (
                            <span className="text-foreground font-medium">
                              {String(action.newValue)}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="text-muted-foreground text-xs whitespace-nowrap">
                        {formatDateTime(action.createdAt)}
                      </span>
                    </div>
                    {index < historyActions.length - 1 && <Separator className="mt-3" />}
                  </div>
                ))}
              </div>
            ))}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
}
